import { flatMap, keyBy, uniqBy } from 'lodash';
import MarkdownIt from 'markdown-it';

import {
	AZURE_FIELDS,
	AzureFieldValue,
	IAzureClassificationNode,
	IAzureField,
	IAzureFields,
	IAzureFieldType,
	IAzureMetaData,
	IAzureProject,
	IAzureWorkItemType,
	OptionType
} from '@evinced-private/ui-common';

import { AzureTicketDraft } from '../types/AzureTicketDraft';
import { IAzureIssueType } from '../types/AzureTypes';

const getPaths = (host: string): string[] => {
	return new URL(host).pathname.split('/');
};

/** The organization/project a `dev.azure.com/{org}/{project}` host points at. */
const getHostSegments = (host: string): { organization: string; project: string } => {
	const [organization, project] = new URL(host).pathname.split('/').filter(Boolean);
	return { organization, project };
};

/** Build an absolute Azure URL from a base origin and path segments (the URL API encodes them). */
const buildAzureUrl = (base: string, ...segments: string[]): string => {
	const url = new URL(base);
	url.pathname = segments.join('/');
	return url.href;
};

const getOrganizationNameUrl = (host: string, apiBaseUrl: string): string => {
	// {apiBaseUrl}/{organization}
	return buildAzureUrl(apiBaseUrl, getHostSegments(host).organization);
};

const getProjectNameUrl = (host: string, apiBaseUrl: string): string => {
	// {apiBaseUrl}/{organization}/{project}
	const { organization, project } = getHostSegments(host);
	return buildAzureUrl(apiBaseUrl, organization, project);
};

const getUsersUrl = (host: string, usersBaseUrl: string): string => {
	// {usersBaseUrl}/{organization}
	return buildAzureUrl(usersBaseUrl, getHostSegments(host).organization);
};

const getProjectsOptionType = (projects: IAzureProject[]): IAzureProject[] => {
	return projects.map((project) => {
		return { ...project, value: project.id, label: project.name };
	});
};

const getProjectTypesOptions = (projectIssueTypes: IAzureIssueType[]): OptionType[] => {
	if (!projectIssueTypes.length) {
		return [];
	}
	// The hidden types are always the last item in the array.
	const hiddenIssueTypes: IAzureWorkItemType[] =
		projectIssueTypes.slice(-1)[0]?.workItemTypes ?? [];
	const projectIssueTypesWithoutHidden: IAzureIssueType[] = projectIssueTypes.slice(0, -1);
	const hiddenTypesSet: Set<string> = new Set<string>(
		hiddenIssueTypes.map((hiddenType: IAzureWorkItemType) => hiddenType.name)
	);
	const filtered: OptionType[] = flatMap(
		projectIssueTypesWithoutHidden,
		(projectIssue: IAzureIssueType) => projectIssue.workItemTypes
	)
		.filter((type: IAzureWorkItemType) => !hiddenTypesSet.has(type.name))
		.map((type: IAzureWorkItemType): OptionType => ({ label: type.name, value: type.name }));

	return uniqBy(filtered, (item: OptionType) => item.label);
};

const getFormattedFields = (
	fields: Record<string, IAzureField>,
	selectedIssueTypeFields: IAzureFieldType[]
): IAzureFields => {
	const mergeFields: IAzureFieldType[] = selectedIssueTypeFields.map((field) => {
		let type: string = fields[field.referenceName]?.type;

		if (field.allowedValues.length || fields[field.referenceName]?.isIdentity) {
			type = 'select';
		} else if (field.referenceName === AZURE_FIELDS.DESCRIPTION || field.type === 'plainText') {
			type = 'textarea';
		} else if (!type || type === 'guid' || type === 'history' || type === 'html') {
			type = 'string';
		}

		return { ...field, type, value: '', label: field.name };
	});

	return keyBy(mergeFields, 'referenceName');
};

const getOverrideFields = (newFields: IAzureFields, previousFields: IAzureFields): IAzureFields => {
	const overrideFields: IAzureFields = { ...newFields };

	// Keep the user's already-entered value when reloading fields for a new issue type.
	Object.keys(overrideFields).forEach((fieldName: string) => {
		const previousFieldValue = previousFields[fieldName]?.value;
		if (overrideFields[fieldName] && previousFieldValue && previousFieldValue !== '') {
			overrideFields[fieldName].value = previousFieldValue;
		}
	});

	return overrideFields;
};

const updateFieldValue = (
	field: IAzureFieldType,
	newValue: AzureFieldValue,
	fields: IAzureFields
): IAzureFields => {
	const updatedFields: IAzureFields = { ...fields };

	updatedFields[field.referenceName] =
		typeof newValue === 'object'
			? { ...field, value: newValue.value }
			: { ...field, value: newValue };

	return updatedFields;
};

const updateFieldsFromIssueData = (
	newFields: IAzureFields,
	reportData: AzureTicketDraft
): IAzureFields => {
	let updatedFields: IAzureFields = { ...newFields };

	if (reportData?.summary) {
		updatedFields = updateFieldValue(
			newFields[AZURE_FIELDS.TITLE],
			reportData.summary,
			updatedFields
		);
	}
	if (reportData?.description) {
		updatedFields = updateFieldValue(
			newFields[AZURE_FIELDS.DESCRIPTION],
			reportData.description,
			updatedFields
		);
	}

	return updatedFields;
};

const getFieldsMetaData = (fields: IAzureFields): IAzureMetaData[] => {
	return Object.values(fields)
		.filter((field) => field.value)
		.map((field) => {
			let { value } = field;
			if (field.referenceName === AZURE_FIELDS.DESCRIPTION) {
				// Azure doesn't understand standard markdown syntax, replacing it with html tags
				const md = new MarkdownIt({
					html: true,
					linkify: true,
					typographer: true
				});
				const formattedString = (field.value as string).replace(
					/{code:html}|{code}|{noformat}/gi,
					'```\n'
				);
				value = md.render(formattedString);
			}
			return {
				op: 'add',
				path: `/fields/${field.referenceName}`,
				value: value as string
			};
		});
};

const replaceProjectName = (host: string, projectName: string, apiBaseUrl: string): string => {
	return buildAzureUrl(apiBaseUrl, getHostSegments(host).organization, projectName);
};

const getClassificationNodes = (nodes: IAzureClassificationNode[]): string[] => {
	const flatten = (arr: IAzureClassificationNode[]): string[] =>
		arr.flatMap(({ path, children = [] }) => [path, ...flatten(children)]);
	const options: string[] = flatten(nodes).map((path): string => {
		// The POST request needs the path without the field name:
		// {project name}\Area\{area path} -> {project name}\{area path}
		const formattedPath = path.includes('Area')
			? path.replace('\\Area', '')
			: path.replace('\\Iteration', '');
		return formattedPath;
	});

	return options;
};

const openUrlInNewTab = (url: string): void => {
	window.open(url, '_blank');
};

const dataURItoBlob = (dataURI: string): Blob => {
	// Decodes a base64 data URL into a Blob. Does not handle URL-encoded data URIs.
	const byteString = atob(dataURI.split(',')[1]);
	const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
	const ab = new ArrayBuffer(byteString.length);
	const ia = new Uint8Array(ab);
	for (let i = 0; i < byteString.length; i++) {
		ia[i] = byteString.charCodeAt(i);
	}
	return new Blob([ab], { type: mimeString });
};

const addAttachmentToPayload = (
	attachmentUrl: string,
	payload: IAzureMetaData[]
): IAzureMetaData[] => {
	const payloadWithAttachment = [...payload];

	if (attachmentUrl) {
		payloadWithAttachment.push({
			op: 'add',
			path: '/relations/-',
			value: {
				rel: 'AttachedFile',
				url: attachmentUrl,
				attributes: {
					comment: 'screenshot'
				}
			}
		});
	}

	return payloadWithAttachment;
};

export {
	buildAzureUrl,
	getProjectNameUrl,
	getClassificationNodes,
	getOrganizationNameUrl,
	getProjectsOptionType,
	getProjectTypesOptions,
	getUsersUrl,
	getPaths,
	getFormattedFields,
	getOverrideFields,
	updateFieldValue,
	updateFieldsFromIssueData,
	openUrlInNewTab,
	replaceProjectName,
	getFieldsMetaData,
	dataURItoBlob,
	addAttachmentToPayload
};
